import React, { useState } from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { CalendarDaysIcon, MapPinIcon, UserIcon } from '@heroicons/react/24/outline';
import { useAcknowledgeIncidentMutation } from '../../store/api/localApi';
import { useToast } from '../Toast';
import { ParentIncident, IncidentDelivery } from '../../types';
import IncidentSeverityChip from './IncidentSeverityChip';

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

const INCIDENT_TYPE_LABELS: Record<string, string> = {
  ACCIDENT: 'Accident',
  INJURY: 'Injury',
  ILLNESS: 'Illness',
  MEDICATION_ERROR: 'Medication error',
  BEHAVIOURAL: 'Behavioural',
  OTHER: 'Other',
};

interface IncidentAcknowledgeCardProps {
  incident: ParentIncident;
  /** Pre-existing acknowledgedAt, if the delivery was already acknowledged
   *  before this page loaded (e.g. a repeat visit). */
  initialAcknowledgedAt?: string;
}

/**
 * Detail card for one incident, plus the Acknowledge action.
 *
 * Acknowledge is an explicit action, not read-tracking — a parent taps a
 * button to confirm they've reviewed it, unlike observation "read" state
 * which is implicit. The backend is idempotent (a repeat POST returns the
 * existing `acknowledgedAt` rather than erroring), so a double-click here
 * must never surface an error state — we render from whichever timestamp
 * we last received, and disable the button once any acknowledgedAt is known.
 */
const IncidentAcknowledgeCard: React.FC<IncidentAcknowledgeCardProps> = ({
  incident,
  initialAcknowledgedAt,
}) => {
  const { showError } = useToast();
  const [acknowledgeIncident, { isLoading: isAcknowledging }] = useAcknowledgeIncidentMutation();
  const [acknowledgedAt, setAcknowledgedAt] = useState<string | undefined>(initialAcknowledgedAt);

  const handleAcknowledge = async () => {
    try {
      const result = await acknowledgeIncident(incident.id).unwrap();
      const delivery = result.delivery as IncidentDelivery;
      setAcknowledgedAt(delivery.acknowledgedAt);
    } catch (err: unknown) {
      const apiError = err as { data?: { error?: string } };
      showError(apiError?.data?.error || 'Failed to acknowledge. Please try again.');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden" data-testid="incident-detail-card">
      <div className="px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-gray-900">
              {INCIDENT_TYPE_LABELS[incident.incidentType] ?? incident.incidentType}
            </h1>
            <p className="text-sm text-gray-500">About {incident.childName}</p>
          </div>
          <IncidentSeverityChip severity={incident.severity} testId="incident-detail-severity" />
        </div>

        <dl className="mt-4 grid grid-cols-1 gap-y-2 sm:grid-cols-2 text-sm text-gray-600">
          <div className="flex items-center gap-1.5">
            <CalendarDaysIcon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
            <dt className="font-medium text-gray-700">Occurred:</dt>
            <dd data-testid="incident-detail-occurred-at">{formatDateTime(incident.occurredAt)}</dd>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
            <dt className="font-medium text-gray-700">Where:</dt>
            <dd data-testid="incident-detail-location">{incident.locationDetail}</dd>
          </div>
          <div className="flex items-center gap-1.5">
            <UserIcon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
            <dt className="font-medium text-gray-700">Reported by:</dt>
            <dd data-testid="incident-detail-reported-by">{incident.reportedByStaffName}</dd>
          </div>
        </dl>
      </div>

      <div className="border-t border-gray-100 px-6 py-5 space-y-4">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            What happened
          </h2>
          <p
            className="text-sm text-gray-800 whitespace-pre-wrap"
            data-testid="incident-detail-description"
          >
            {incident.description}
          </p>
        </div>

        {incident.injuryDetail && (
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Injury
            </h2>
            <p
              className="text-sm text-gray-800 whitespace-pre-wrap"
              data-testid="incident-detail-injury"
            >
              {incident.injuryDetail}
            </p>
          </div>
        )}

        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Action taken
          </h2>
          <p
            className="text-sm text-gray-800 whitespace-pre-wrap"
            data-testid="incident-detail-action-taken"
          >
            {incident.actionTaken}
          </p>
        </div>

        {incident.followUp && (
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Follow-up
            </h2>
            <p
              className="text-sm text-gray-800 whitespace-pre-wrap"
              data-testid="incident-detail-follow-up"
            >
              {incident.followUp}
            </p>
          </div>
        )}
      </div>

      {incident.attachments.length > 0 && (
        <div className="border-t border-gray-100 px-6 py-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
            Photos
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {incident.attachments.map((a, idx) =>
              a.url ? (
                <a
                  key={a.s3Key}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`incident-detail-photo-${idx}`}
                  className="block rounded overflow-hidden border border-gray-200 hover:opacity-90"
                >
                  <img
                    src={a.url}
                    alt={`Incident attachment ${idx + 1}`}
                    className="object-cover w-full h-32"
                  />
                </a>
              ) : null
            )}
          </div>
        </div>
      )}

      <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex items-center justify-end">
        {acknowledgedAt ? (
          <p className="text-sm text-gray-600" data-testid="incident-acknowledged-at">
            Acknowledged {formatDateTime(acknowledgedAt)}
          </p>
        ) : (
          <button
            type="button"
            data-testid="incident-acknowledge-btn"
            onClick={handleAcknowledge}
            disabled={isAcknowledging}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {isAcknowledging ? 'Acknowledging…' : 'Acknowledge'}
          </button>
        )}
      </div>
    </div>
  );
};

export default IncidentAcknowledgeCard;
